import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';
import { 
  Ability,
  Character,
  CharacterAbility,
  CharacterSheetInterface,
  HitDice,
} from "@dndModels";

import CommonUtilities from "@utilities/common_utilities";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import DataUtilities from '@dndUtilities/data_utilities';
// import DandDAPI from '../../../utilities/system_api';

import ButtonBox from "@components/input/buttons/ButtonBox";
import CheckBox from "@components/input/CheckBox";
import CenteredMenu from "@components/input/menus/CenteredMenu";
import CharacterActionInput from "@dndComponents/character/spells_and_abilities/CharacterActionInput";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import ChargesRefreshContol from "@dndComponents/character/misc/ChargesRefreshControl";


interface Props {
  char: Character; 
  char_interface: CharacterSheetInterface;
  // onChange: () => void;
}

const common_util = CommonUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const data_util = DataUtilities.getInstance();
// const api = DandDAPI.getInstance();

export default function CharacterRest({
  char,
  char_interface,
  // onChange,
}: Props) {
  const [view, setView] = useState<"Mini Rest" | "Short Rest" | "Long Rest" | "Night Rest">(char.character_options.use_mini_night_rests ? "Mini Rest" : "Short Rest");
  const [hd_selected, setHDSelected] = useState<{ [key: number]: number }>({});
  const [messages, setMessages] = useState<string[]>([]);
  const [auto_heal, setAutoHeal] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [expanded_action_id, setExpandedActionId] = useState("");

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading]);
  
  const renderMessages = () => {
    return messages.map((message, key) => {
      return (
        <Grid
          key={key}
          style={{ fontSize: "16px", fontWeight: "bold" }}
          size={{
            xs: 12
          }}>
          { message }
        </Grid>
      );
    });
  }

  const renderHitDiceControls = () => {
    // For now, I'm just going to have these controls here for keeping track
    // but eventually for short rest it will add controls to heal based on selected
    // and for long rest it will be automatic.
    return char_interface.hit_dice.map((hd, key) => {
      const selected = hd_selected[hd.size] ? hd_selected[hd.size] : 0;
      const return_me: any[] = [];
      for (let i = 0; i < hd.count; ++i) {
        return_me.push(
          <CheckBox key={i} name="" 
            value={ hd.used > i ? null : hd.used + selected > i } 
            onChange={() => {
              const new_hd_selected = {...hd_selected};
              if ((hd.used + selected) > i && selected > 0) {
                new_hd_selected[hd.size] = selected - 1;
              } else if ((hd.used + selected) <= i) {
                new_hd_selected[hd.size] = selected + 1;
              }
              setHDSelected(new_hd_selected);
            }}
          />
        );
      }
      return (
        <Grid key={key} container spacing={0} direction="column">
          <Grid>
            { char_interface instanceof Character && char_interface.classes.filter(o => o.source && o.source.hit_die === hd.size).map((char_class, key2) => {
              return (
                <span key={key2}>{ key2 > 0 ? "/" : "" }{ char_class.name }</span>
              );
            }) } (Hit Die: 1d{ hd.size }+{ char_interface.current_ability_scores.CON }; Total:{ hd.count })
          </Grid>
          <Grid>
            { return_me }
          </Grid>
        </Grid>
      );
    });
  }

  const refreshResources = (long_rest: boolean): string[] => {
    // debugger;
    const new_messages: string[] = [];
    const refreshing_rules = long_rest ? ["Short Rest","Long Rest","Dawn","1 Hour","2 Hours","8 Hours"] : ["Short Rest","1 Hour"];
    for (const char_ability of char.abilities.filter(o => 
      refreshing_rules.includes(o.special_resource_refresh_rule)
    )) {
      char.resource_holder.setSpecialResourceUsed(char_ability.true_id, 0);
    }
    for (const char_ability of char.spell_as_abilities.filter(o =>
      refreshing_rules.includes(o.special_resource_refresh_rule)
    )) {
      char.resource_holder.setSpecialResourceUsed(char_ability.true_id, 0);
    }
    for (const char_ability of char.extra_effects.filter(o => 
      refreshing_rules.includes(o.special_resource_refresh_rule)
    )) {
      char.resource_holder.setSpecialResourceUsed(char_ability.true_id, 0);
    }
    for (const char_resource of char.resources.filter(o => 
      !o.is_slot &&
      (o.used !== 0 || o.created !== 0) &&
      refreshing_rules.includes(o.refresh_rule)
    )) {
      char_resource.used = 0;
      char_resource.created = 0;
      new_messages.push(`${char_resource.name} refreshed`);
    }
    if (long_rest) {
      for (const char_resource of char.resources.filter(o => 
        !o.is_slot &&
        (o.used !== 0 || o.created !== 0) &&
        o.refresh_rule === "Long Rest" &&
        !o.refresh_x_on_short.isBlank()
      )) {
        const refresh_x_on_short = char_util.value(char_resource.refresh_x_on_short, { obj: char });
        char_resource.used = Math.max(0, char_resource.used - refresh_x_on_short);
        new_messages.push(`${char_resource.name} refreshed`);
      }
      char.removeSlots("Created"); // .use_slots = character.use_slots.filter(o => !o.type_id.includes("Created"));
      for (const char_slot of char.use_slots.filter(o => o.used !== 0 || o.created !== 0)) {
        char_slot.used = 0;
        char_slot.created = 0;
        if (char_slot.is_resource) {
          const char_resource = char.resources.find(o => o.type_id === char_slot.type_id);
          if (char_resource) {
            char_resource.used = 0;
            char_resource.created = 0;
            new_messages.push(`${char_resource.name} refreshed`);
          }
        }
      }
      for (const item of char.items.filter(o => o.infusions.length > 0)) {
        for (const infusion of item.infusions) {
          infusion.used = false;
        }
      }
    } else {
      char.removeSlots("Created (Pact)"); // .use_slots = character.use_slots.filter(o => o.type_id !== "Created (Pact)");
      for (const char_slot of char.use_slots.filter(o => o.used !== 0 || o.created !== 0)) {
        const slot_type = data_util.spell_slot_types.find(o => o._id === char_slot.type_id);
        if (slot_type && slot_type.refresh_rule === "Short Rest") {
          char_slot.used = 0;
          new_messages.push(`${char_slot.slot_name} refreshed`);
        }
      }
    }
    for (const char_resource of char.resources.filter(o => 
      o.zero_on_rest && o.used !== o.total
    )) {
      char_resource.created = 0;
      char_resource.used = char_resource.total;
      new_messages.push(`${char_resource.name} zeroed`);
    }
    return new_messages;
  }

  const renderView = () => {
    if (view === "Long Rest") {
      if (char.character_options.use_mini_night_rests) {
        return (
          <Grid
            container
            spacing={0}
            direction="column"
            style={{ margin: "4px" }}
            size={{
              xs: 12
            }}>
            <Grid style={{ fontSize: "10px" }}>
              { char.character_options.long_rest_description }
            </Grid>
            <Grid style={{ fontWeight: "bold" }}>
              Recover
            </Grid>
            <Grid style={{ fontSize: "10px" }}>
              Most resources automatically reset to their max 
              on a long rest.
            </Grid>
            { char_interface.hit_dice.filter(o => o.used > 0).length > 0 &&
              <Grid style={{ fontSize: "10px" }}>
                You will recover { Math.min(Math.floor(
                  char.character_level / 2), 
                  char_interface.hit_dice.reduce((accumulator: number, hd: HitDice) => {
                    return accumulator + hd.used;
                  }, 0)) 
                } Hit Dice on this long rest.  (If you have hit dice of different sizes, you will recover the largest first.)
              </Grid>
            }
            <Grid>
              <ButtonBox name="Take a Long Rest" 
                onClick={() => {
                  let new_messages: string[] = [];
                  // Clear max hit point changes and temp hp
                  if (char.hp.max_modifier !== 0 || 
                    char.hp.override_max !== null) {
                    char.hp.max_modifier = 0;
                    char.hp.override_max = null;
                    new_messages.push("Your Max Hit Point changes have been removed.");
                  }
                  if (char.hp.temp > 0) {
                    char.hp.temp = 0;
                    new_messages.push("You've lost your Temporary Hit Points.");
                  }
                  new_messages.push("You've been fully healed.");
                  // Full heal
                  char.hp.current = char.hp.max;
                  const hit_dice = char.hit_dice.sort((a,b) => {return a.size - b.size;});
                  let restore_hd = Math.floor(char.character_level / 2);
                  for (let i = 0; i < hit_dice.length; ++i) {
                    const hd = hit_dice[i];
                    if (hd.used > 0) {
                      if (hd.used >= restore_hd) {
                        new_messages.push(`You've recovered ${restore_hd} of your d${hd.size} Hit Dice`);
                        hd.used -= restore_hd;
                        restore_hd = 0;
                      } else {
                        new_messages.push(`You've recovered ${hd.used} of your d${hd.size} Hit Dice`);
                        restore_hd -= hd.used;
                        hd.used = 0;
                      }
                      if (restore_hd === 0) {
                        break;
                      }
                    }
                  }
                  new_messages = [...new_messages,...refreshResources(true)];
                  const conditions = data_util.conditions;
                  // debugger;
                  // const cond_ids = Object.keys(char.condition_hash);
                  // for (const cond_id of cond_ids) {
                  //   const condition = conditions.find(o => o._id === cond_id);
                  //   if (condition && condition.reduce_on_long_rest > 0) {
                  //     char.condition_hash[cond_id].level -= condition.reduce_on_long_rest;
                  //     if (char.condition_hash[cond_id].level <= 0) {
                  //       delete char.condition_hash[cond_id];
                  //       new_messages.push(`Your ${condition.name} has been removed.`);
                  //     } else {
                  //       new_messages.push(`Your ${condition.name} has been reduced by ${condition.reduce_on_long_rest}.`);
                  //     }
                  //   }
                  // }
                  const remove_us: string[] = [];
                  for (const char_cond of char.conditions) {
                    const condition = conditions.find(o => o._id === char_cond.source_id);
                    if (condition && condition.reduce_on_long_rest > 0) {
                      char_cond.level -= condition.reduce_on_long_rest;
                      if (char_cond.level <= 0) {
                        remove_us.push(char_cond.true_id);
                        new_messages.push(`Your ${condition.name} has been removed.`);
                      } else {
                        new_messages.push(`Your ${condition.name} has been reduced by ${condition.reduce_on_long_rest}.`);
                      }
                    }
                  }
                  if (remove_us.length > 0) {
                    char.conditions = char.conditions.filter(o => !remove_us.includes(o.true_id));
                  }
                  setMessages(new_messages);
                  setHDSelected({});
                  char.changed();
                  char_util.repopulateCharFeatureBases(char);
                }} 
              />
            </Grid>
          </Grid>
        );
      } else {
        return (
          <Grid
            container
            spacing={0}
            direction="column"
            style={{ margin: "4px" }}
            size={{
              xs: 12
            }}>
            <Grid style={{ fontSize: "10px" }}>
              { char.character_options.long_rest_description }
            </Grid>
            <Grid style={{ fontWeight: "bold" }}>
              Recover
            </Grid>
            <Grid style={{ fontSize: "10px" }}>
              Most resources automatically reset to their max 
              on a long rest.
            </Grid>
            { char_interface.hit_dice.filter(o => o.used > 0).length > 0 &&
              <Grid style={{ fontSize: "10px" }}>
                You will recover { Math.min(Math.floor(
                  char.character_level / 2), 
                  char_interface.hit_dice.reduce((accumulator: number, hd: HitDice) => {
                    return accumulator + hd.used;
                  }, 0)) 
                } Hit Dice on this long rest.  (If you have hit dice of different sizes, you will recover the largest first.)
              </Grid>
            }
            <Grid>
              <ButtonBox name="Take a Long Rest" 
                onClick={() => {
                  let new_messages: string[] = [];
                  // Clear max hit point changes and temp hp
                  if (char.hp.max_modifier !== 0 || 
                    char.hp.override_max !== null) {
                    char.hp.max_modifier = 0;
                    char.hp.override_max = null;
                    new_messages.push("Your Max Hit Point changes have been removed.");
                  }
                  if (char.hp.temp > 0) {
                    char.hp.temp = 0;
                    new_messages.push("You've lost your Temporary Hit Points.");
                  }
                  new_messages.push("You've been fully healed.");
                  // Full heal
                  char.hp.current = char.hp.max;
                  const hit_dice = char.hit_dice.sort((a,b) => {return a.size - b.size;});
                  let restore_hd = Math.floor(char.character_level / 2);
                  for (let i = 0; i < hit_dice.length; ++i) {
                    const hd = hit_dice[i];
                    if (hd.used > 0) {
                      if (hd.used >= restore_hd) {
                        new_messages.push(`You've recovered ${restore_hd} of your d${hd.size} Hit Dice`);
                        hd.used -= restore_hd;
                        restore_hd = 0;
                      } else {
                        new_messages.push(`You've recovered ${hd.used} of your d${hd.size} Hit Dice`);
                        restore_hd -= hd.used;
                        hd.used = 0;
                      }
                      if (restore_hd === 0) {
                        break;
                      }
                    }
                  }
                  new_messages = [...new_messages,...refreshResources(true)];
                  const conditions = data_util.conditions;
                  // debugger;
                  // const cond_ids = Object.keys(char.condition_hash);
                  // for (const cond_id of cond_ids) {
                  //   const condition = conditions.find(o => o._id === cond_id);
                  //   if (condition && condition.reduce_on_long_rest > 0) {
                  //     char.condition_hash[cond_id].level -= condition.reduce_on_long_rest;
                  //     if (char.condition_hash[cond_id].level <= 0) {
                  //       delete char.condition_hash[cond_id];
                  //       new_messages.push(`Your ${condition.name} has been removed.`);
                  //     } else {
                  //       new_messages.push(`Your ${condition.name} has been reduced by ${condition.reduce_on_long_rest}.`);
                  //     }
                  //   }
                  // }
                  const remove_us: string[] = [];
                  for (const char_cond of char.conditions) {
                    const condition = conditions.find(o => o._id === char_cond.source_id);
                    if (condition && condition.reduce_on_long_rest > 0) {
                      char_cond.level -= condition.reduce_on_long_rest;
                      if (char_cond.level <= 0) {
                        remove_us.push(char_cond.true_id);
                        new_messages.push(`Your ${condition.name} has been removed.`);
                      } else {
                        new_messages.push(`Your ${condition.name} has been reduced by ${condition.reduce_on_long_rest}.`);
                      }
                    }
                  }
                  if (remove_us.length > 0) {
                    char.conditions = char.conditions.filter(o => !remove_us.includes(o.true_id));
                  }
                  setMessages(new_messages);
                  setHDSelected({});
                  char.changed();
                  char_util.repopulateCharFeatureBases(char);
                }} 
              />
            </Grid>
          </Grid>
        );
      }
    } else if (view === "Short Rest") {
      return (
        <Grid
          container
          spacing={0}
          direction="column"
          style={{ margin: "4px" }}
          size={{
            xs: 12
          }}>
          <Grid style={{ fontSize: "10px" }}>
            { char.character_options.short_rest_description }
          </Grid>
          <Grid style={{ fontWeight: "bold" }}>
            Recover
          </Grid>
          <Grid style={{ fontSize: "10px" }}>
            Many resources automatically reset to their max 
            on a short rest.
          </Grid>
          <Grid style={{ fontSize: "10px" }}>
            Other resources have optional resetting, but can 
            only be done a certain number of times.
          </Grid>
          <Grid style={{ fontSize: "10px" }}>
            { renderHitDiceControls() }
          </Grid>
          { some_selected && 
            <Grid>
              <CheckBox name="Auto Heal on Rest" 
                value={auto_heal}
                onChange={() => {
                  setAutoHeal(!auto_heal);
                }}
              />
            </Grid>
          }
          <Grid>
            <ButtonBox name="Take a Short Rest" 
              onClick={() => {
                // Roll selected Hit Die, and heal
                const new_hd_selected = {...hd_selected};
                let total_healing = 0;
                let new_messages: string[] = [];
                const CON = char.current_ability_scores.CON;
                Object.keys(new_hd_selected).forEach((the_size: string) => {
                  const size = +the_size;
                  const selected = new_hd_selected[size] as number;
                  if (auto_heal) {
                    for (let i = 0; i < selected; ++i) {
                      const rolled = common_util.get_random(size);
                      total_healing += rolled + CON;
                      new_messages.push(`Healing for 1d${size}(${rolled})+${CON} = ${rolled + CON}`);
                    }
                  }
                  const hd_finder = char.hit_dice.filter(o => o.size === size);
                  if (hd_finder.length === 1) {
                    const hit_die = hd_finder[0];
                    hit_die.used += selected;
                  }
                });
                if (total_healing > 0) {
                  new_messages.push(`Total Healing: ${total_healing}`);
                  const max = (char.hp.override_max !== null ? char.hp.override_max : char.hp.max) + char.hp.max_modifier;
                  char.hp.current = Math.min(char.hp.current + total_healing, max);
                }
                new_messages = [...new_messages,...refreshResources(false)];
                
                setMessages(new_messages);
                setHDSelected({});
                char.changed();
                char_util.repopulateCharFeatureBases(char);
              }} 
            />
          </Grid>
        </Grid>
      );
    } else if (view === "Night Rest") {
      return (
        <Grid
          container
          spacing={0}
          direction="column"
          style={{ margin: "4px" }}
          size={{
            xs: 12
          }}>
          <Grid style={{ fontSize: "10px" }}>
            { char.character_options.night_rest_description }
          </Grid>
          <Grid style={{ fontWeight: "bold" }}>
            Recover
          </Grid>
          <Grid style={{ fontSize: "10px" }}>
            Most resources automatically reset to their max 
            on a night rest, and hit dice can be used to 
            recover hit points.
          </Grid>
          <Grid style={{ fontSize: "10px" }}>
            { renderHitDiceControls() }
          </Grid>
          { some_selected && 
            <Grid>
              <CheckBox name="Auto Heal on Rest" 
                value={auto_heal}
                onChange={() => {
                  setAutoHeal(!auto_heal);
                }}
              />
            </Grid>
          }
          <Grid>
            <ButtonBox name="Take a Night Rest" 
              onClick={() => {
                // Roll selected Hit Die, and heal
                const new_hd_selected = {...hd_selected};
                let total_healing = 0;
                let new_messages: string[] = [];
                const CON = char.current_ability_scores.CON;
                Object.keys(new_hd_selected).forEach((the_size: string) => {
                  const size = +the_size;
                  const selected = new_hd_selected[size] as number;
                  if (auto_heal) {
                    for (let i = 0; i < selected; ++i) {
                      const rolled = common_util.get_random(size);
                      total_healing += rolled + CON;
                      new_messages.push(`Healing for 1d${size}(${rolled})+${CON} = ${rolled + CON}`);
                    }
                  }
                  const hd_finder = char.hit_dice.filter(o => o.size === size);
                  if (hd_finder.length === 1) {
                    const hit_die = hd_finder[0];
                    hit_die.used += selected;
                  }
                });
                if (total_healing > 0) {
                  new_messages.push(`Total Healing: ${total_healing}`);
                  const max = (char.hp.override_max !== null ? char.hp.override_max : char.hp.max) + char.hp.max_modifier;
                  char.hp.current = Math.min(char.hp.current + total_healing, max);
                }
                new_messages = [...new_messages,...refreshResources(true)];
                setMessages(new_messages);
                setHDSelected({});
                char.changed();
                char_util.repopulateCharFeatureBases(char);
              }} 
            />
          </Grid>
        </Grid>
      );
    } else { // if (view === "Mini Rest") {
      return (
        <Grid
          container
          spacing={0}
          direction="column"
          style={{ margin: "4px" }}
          size={{
            xs: 12
          }}>
          <Grid style={{ fontSize: "10px" }}>
            { char.character_options.mini_rest_description }
          </Grid>
          <Grid style={{ fontWeight: "bold" }}>
            Recover
          </Grid>
          <Grid style={{ fontSize: "10px" }}>
            Many resources automatically reset to their max 
            on a mini rest.
          </Grid>
          <Grid style={{ fontSize: "10px" }}>
            Other resources have optional resetting, but can 
            only be done a certain number of times.
          </Grid>
          <Grid>
            <ButtonBox name="Take a Mini Rest" 
              onClick={() => {
                let new_messages: string[] = [];
                new_messages = [...new_messages,...refreshResources(false)];
                setMessages(new_messages);
                setHDSelected({});
                char.changed();
                char_util.repopulateCharFeatureBases(char);
              }} 
            />
          </Grid>
        </Grid>
      );
    }
  }

  // const onReload = () => {
  //   setReloading(true);
  // }

  const renderSpecial = () => {
    // debugger;
    const rest_type = view === "Night Rest" ? "Long Rest" : (view === "Mini Rest" ? "Short Rest" : view);
    const filtered = char.actions.filter(o => 
      o.action instanceof CharacterAbility &&
      o.action.the_ability instanceof Ability &&
      o.action.the_ability.casting_time === rest_type);
    
    if (filtered.length > 0) {
      return (
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 12
          }}>
          <Grid
            style={{
              fontSize: "14px",
            }}
            className={`header`}
            size={{
              xs: 12
            }}>
            &nbsp;Resting Abilities
          </Grid>
          { filtered.map((action, key: number) => {
            if (reloading) {
              return (<span key={key}></span>);
            } else {
              return (
                <Grid
                  key={key}
                  container
                  spacing={0}
                  direction="row"
                  size={{
                    xs: 12
                  }}>
                  <Grid
                    size={{
                      xs: 12
                    }}>
                    <CharacterActionInput
                      char={char}
                      char_interface={char}
                      action={action}
                      group={action.casting_time}
                      processing={false}
                      shared={false}
                      level={action.level}
                      onOpen={() => {
                        setExpandedActionId(expanded_action_id === action.true_id ? "" : action.true_id);
                      }}
                    />
                  </Grid>
                  { expanded_action_id === action.true_id && 
                    <Grid
                      className="padded"
                      size={{
                        xs: 12
                      }}>
                      <DisplayStringWithReplacements display_string={ action.description } />
                    </Grid>
                  }
                </Grid>
              );
            }
          })}
        </Grid>
      );
    }
    return null;
  }

  const renderCharges = () => {
    const rest_type = view === "Night Rest" ? "Long Rest" : (view === "Mini Rest" ? "Short Rest" : view);
    const refreshing_rules = rest_type === "Long Rest" ? ["Short Rest","Long Rest","Dawn","1 Hour","2 Hours","8 Hours"] : ["Short Rest","1 Hour"];
    const filtered = char.items.filter(o =>
      o.item_type !== "Potion" && o.item_type !== "Scroll" && o.item_type !== "Dust" &&
      !o.invert_charges &&
      o.max_charges > 0 && refreshing_rules.includes(o.charge_refresh_rule));
      
    // console.log(rest_type);
    // console.log(refreshing_rules);
    // console.log(filtered);
    if (filtered.length > 0) {
      return (
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 12
          }}>
          <Grid
            style={{
              fontSize: "14px",
            }}
            className={`header`}
            size={{
              xs: 12
            }}>
            &nbsp;Charges
          </Grid>
          { filtered.map(char_item => {
            if (reloading) {
              return (
                <Grid
                  key={char_item.true_id}
                  size={{
                    xs: 12
                  }}></Grid>
              );
            } else {
              return (
                <Grid
                  key={char_item.true_id}
                  size={{
                    xs: 12
                  }}>
                  <ChargesRefreshContol 
                    character={char} 
                    char_interface={char_interface} 
                    char_item={char_item} 
                    // onChange={() => {
                    //   character.changed();
                    //   char_util.repopulateCharFeatureBases(character);
                    // }}
                  />
                </Grid>
              );
            }
          })}
        </Grid>
      );
    }
    return null;
  }

  const selected_keys = Object.keys(hd_selected);
  let some_selected = false;
  for (let i = 0; i < selected_keys.length; ++i) {
    if (+hd_selected[+selected_keys[i]] > 0) {
      some_selected = true;
      break;
    }
  }
  const views = char.character_options.use_mini_night_rests ? ["Mini Rest","Night Rest","Long Rest"] : ["Short Rest","Long Rest"];
  return (
    <Grid container spacing={0} direction="row">
      <Grid
        size={{
          xs: 12
        }}>
        <CenteredMenu
          options={views}
          selected={view}
          onSelect={(changed: string) => {
            setView(changed as "Mini Rest" | "Short Rest" | "Long Rest" | "Night Rest");
          }}
        />
      </Grid>
      { renderView() }
      { renderMessages() }
      { renderSpecial() }
      { renderCharges() }
    </Grid>
  );
}
